import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Grid,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import SyncIcon from '@mui/icons-material/Sync';
import CardsSidebar from '../../components/Cards/CardsSidebar';
import CardsTable from '../../components/Cards/CardsTable';
import CardsControlButtons from '../../components/Cards/CardsControlButtons';
import Breadcrumb from '../../components/common/Breadcrumb';
import useApi from '../../hooks/useApi';
import { useTenant } from '../../contexts/TenantContext';
import { useProjectProgress, CHANGE_TYPES } from '../../contexts/ProjectProgressContext';
import branding from '../../styles/branding';
import { useCustomAlert, useErrorToast } from '../../components/common';
import SummarizeData from '../../components/Widgets/SummarizeData';
import AIRecommendation from '../../components/Widgets/AIRecommendation';
import AnalyzeMajorIssues from '../../components/Widgets/AnalyzeMajorIssues';
import SuccessFactors from '../../components/Widgets/SuccessFactors';
import CustomAICard from '../../components/Widgets/CustomAICard';
import BuildBarChart from '../../components/Widgets/BuildBarChart';
import BuildPieChart from '../../components/Widgets/BuildPieChart';
import BuildKPI from '../../components/Widgets/BuildKPI';
import WidgetResultsRenderer from '../../components/Widgets/WidgetResultsRenderer';
import { formatBusinessText } from '../../utils/textFormatting';
import { pivotStackedBarLongRows } from '../../utils/barChartFormat';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { toEnum, toDisplay, getPopupIdFromWidgetType } from '../../constants/widgetTypes';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { PieChart, Pie, Cell, Legend } from 'recharts';
import './CardsPage.css';

const CHART_COLORS = [
  'rgb(64, 121, 141)',
  'rgb(209, 185, 167)',
  'rgb(149, 198, 213)',
  'rgb(145, 89, 145)',
  'rgb(165, 198, 165)',
  'rgb(125, 111, 100)',
  'rgb(170, 188, 244)',
  'rgb(73, 119, 73)',
  'rgb(208, 181, 208)',
  'rgb(102, 117, 121)'
];
const BREADCRUMB_STORAGE_KEY_PREFIX = 'breadcrumb_max_step_';
const DASHBOARD_STEP_INDEX = 4;

const CardsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { currentTenant } = useTenant();
  const {
    updateCurrentStep,
    markUnsavedChanges,
    trackChange,
    saveProjectProgress,
    clearChangesTracker,
    registerSaveBeforeLeave,
    unregisterSaveBeforeLeave,
    currentProject,
    initializeProject,
    setIsEditingDashboard,
    markProjectAsSaved,
  } = useProjectProgress();
  const { showAlert } = useCustomAlert();
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    updateCurrentStep('cards');
  }, [updateCurrentStep]);

  // Rehydrate project from URL when on Cards with jobId but no context (e.g. refresh, direct link) so save-before-leave can show
  useEffect(() => {
    if (jobId && !currentProject) {
      initializeProject(
        { projectId: jobId, projectName: 'Project', currentStep: 'cards' },
        { markUnsaved: false }
      );
    }
  }, [jobId, currentProject, initializeProject]);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [projectName, setProjectName] = useState('Dashboard for Survey Responses');
  const [pendingWidgets, setPendingWidgets] = useState([]);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isLoadingWidgets, setIsLoadingWidgets] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSavingCards, setIsSavingCards] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(jobId);
  const [viewCardDialog, setViewCardDialog] = useState({ open: false, row: null, payload: null, loading: false });
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editInitialData, setEditInitialData] = useState(null);
  const loadedJobIdRef = useRef(null);
  // Add to folder (Create dashboard): require at least one folder
  const [addToFolderDialogOpen, setAddToFolderDialogOpen] = useState(false);
  const [addToFolderSelectedIds, setAddToFolderSelectedIds] = useState([]);
  const [addToFolderFoldersFlat, setAddToFolderFoldersFlat] = useState([]);
  const [addToFolderLoading, setAddToFolderLoading] = useState(false);
  const [addToFolderUserId, setAddToFolderUserId] = useState(null);
  const [addToFolderNewFolderName, setAddToFolderNewFolderName] = useState('');
  const [addToFolderCreateParentId, setAddToFolderCreateParentId] = useState('');
  const [addToFolderCreateSubmitting, setAddToFolderCreateSubmitting] = useState(false);
  const [addToFolderCreateError, setAddToFolderCreateError] = useState(null);
  
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSyncResponses = async () => {
    if (!currentProject || !currentProject.projectId) return;
    setIsSyncing(true);
    try {
      const response = await api.post('/jobs/refresh-survey', {
        projectId: currentProject.projectId
      });
      if (response.status === 200 || response.status === 201) {
        // Re-run AI cards if any exist
        const aiTypes = new Set(['recommendation', 'major_issues', 'summary', 'success', 'custom_ai_card', 'analysis', 'custom_ai']);
        const aiCards = tableData.filter(card => aiTypes.has(card.widgetData?.widgetType));

        if (aiCards.length > 0) {
          const executePromises = aiCards.map(card => {
            const requestData = {
              jobId: currentProject.projectId,
              widgetType: card.widgetData.widgetType,
              selectedColumns: card.widgetData.selectedColumns || [],
              widgetName: card.widgetData.widgetName || card.cardName,
              widgetId: card.widgetId
            };
            if (card.widgetData.prompt) {
              requestData.customPrompt = card.widgetData.prompt;
            }
            // Pass along model and promptType if they exist in widgetData
            if (card.widgetData.model) requestData.model = card.widgetData.model;
            if (card.widgetData.promptType) requestData.promptType = card.widgetData.promptType;

            return api.post("/widgets/execute", requestData).then(res => ({
              cardId: card.widgetId,
              result: res.data?.data || res.data
            })).catch(err => {
              console.error(`Failed to execute AI for card ${card.widgetId}:`, err);
              return { cardId: card.widgetId, result: null, error: err };
            });
          });

          const results = await Promise.all(executePromises);

          setTableData(prevData => {
            const nextData = [...prevData];
            results.forEach(({ cardId, result, error }) => {
              if (error || !result) return;
              const index = nextData.findIndex(c => c.widgetId === cardId);
              if (index !== -1) {
                const updatedCard = { ...nextData[index] };
                updatedCard.widgetData = {
                  ...updatedCard.widgetData,
                  widgetResults: result
                };
                if (result.tags) {
                  updatedCard.tags = normalizeTags(result.tags);
                  updatedCard.widgetData.tags = updatedCard.tags;
                }
                nextData[index] = updatedCard;
              }
            });
            return nextData;
          });
        }

        showAlert('Survey responses synced successfully!', { severity: 'success' });
        // Optional: you could reload the widgets here if they depend on fresh data
      } else {
        throw new Error(response.data?.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Failed to sync responses:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to sync responses.'));
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (loadingError) {
      showErrorToast(loadingError);
      setLoadingError(null);
    }
  }, [loadingError, showErrorToast]);

  useEffect(() => {
    if (addToFolderCreateError) {
      showErrorToast(addToFolderCreateError);
      setAddToFolderCreateError(null);
    }
  }, [addToFolderCreateError, showErrorToast]);
  useEffect(() => {
    setIsEditingDashboard(isEditingMode);
  }, [isEditingMode, setIsEditingDashboard]);
  const persistDashboardBreadcrumbUnlock = useCallback((identifier) => {
    if (!identifier) return;
    try {
      const key = `${BREADCRUMB_STORAGE_KEY_PREFIX}${identifier}`;
      const stored = window.localStorage.getItem(key);
      const parsed = stored != null ? parseInt(stored, 10) : 0;
      const fromStorage = Number.isNaN(parsed) ? 0 : parsed;
      const nextMax = Math.max(fromStorage, DASHBOARD_STEP_INDEX);
      window.localStorage.setItem(key, String(nextMax));
    } catch {
      // ignore localStorage failures
    }
  }, []);
  const normalizeTags = (rawTags) => {
    if (!rawTags) return [];
    if (Array.isArray(rawTags)) return rawTags.filter(Boolean);
    if (typeof rawTags !== 'string') return [];
    const trimmed = rawTags.trim();
    if (!trimmed) return [];
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.filter(Boolean);
        }
      } catch (error) {
        console.warn('Failed to parse tags JSON:', error);
      }
    }
    return trimmed.split(',').map(tag => tag.trim()).filter(Boolean);
  };
  /** Ref so popup callback sees current edit context (setState is async; popup may close over stale editingRowIndex) */
  const editingContextRef = useRef({ index: null, widgetId: null });

  function buildFolderTree(flatList) {
    if (!flatList?.length) return [];
    const byId = {};
    flatList.forEach((f) => {
      byId[f.FolderId] = { ...f, children: [] };
    });
    const roots = [];
    flatList.forEach((f) => {
      const node = byId[f.FolderId];
      if (!f.ParentFolderId || !byId[f.ParentFolderId]) roots.push(node);
      else byId[f.ParentFolderId].children.push(node);
    });
    return roots;
  }
  function flattenFoldersForPicker(nodes, list = []) {
    nodes.forEach((n) => {
      list.push(n);
      if (n.children?.length) flattenFoldersForPicker(n.children, list);
    });
    return list;
  }

  const loadAddToFolderFolders = useCallback(async () => {
    setAddToFolderLoading(true);
    try {
      const res = await api.get('/folder');
      const raw = res.data?.data;
      const list = Array.isArray(raw) ? raw : [];
      const tree = buildFolderTree(list);
      setAddToFolderFoldersFlat(flattenFoldersForPicker(tree));
    } catch (e) {
      console.error('Load folders failed:', e);
      setAddToFolderFoldersFlat([]);
    } finally {
      setAddToFolderLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!addToFolderDialogOpen || !currentTenant?.tenantId) return undefined;
    (async () => {
      try {
        const res = await api.get('/auth', { headers: { 'X-Tenant-ID': currentTenant.tenantId } });
        if (res.data?.success && res.data?.data?.user?.userId) {
          setAddToFolderUserId(res.data.data.user.userId);
        }
      } catch (e) {
        console.warn('Could not load user for folder creation:', e);
      }
    })();
    return undefined;
  }, [addToFolderDialogOpen, currentTenant?.tenantId, api]);

  useEffect(() => {
    registerSaveBeforeLeave(async () => {
      if (tableData.length === 0) return { success: true };
      return performCreateDashboard({ skipNavigate: true });
    });
    return () => unregisterSaveBeforeLeave();
  }, [registerSaveBeforeLeave, unregisterSaveBeforeLeave, tableData.length]);

  // Load existing widgets when component mounts or jobId changes
  useEffect(() => {
    const loadExistingWidgets = async () => {
      console.log('Cards component mounted, jobId:', jobId);
      
      setCurrentJobId(jobId);
      
      if (!jobId) {
        console.log('No jobId found, starting in new dashboard mode');
        setTableData([]);
        setPendingWidgets([]);
        setIsEditingMode(false);
        setLoadingError(null);
        return;
      }

      if (loadedJobIdRef.current === jobId) {
        console.log('✅ Cards already loaded for this job, maintaining state');
        return;
      }
      
      if (loadedJobIdRef.current && loadedJobIdRef.current !== jobId) {
        console.log('Switching to different job, clearing state');
      }
      
      console.log('JobId found, loading widgets for job...');
      
      setTableData([]);
      setPendingWidgets([]);
      setIsEditingMode(false);
      setLoadingError(null);
      
      loadedJobIdRef.current = jobId;
      setIsLoadingWidgets(true);

      try {
        // Fetch project/job information to get the project name
        // Identifier can be projectId, fileName, or tableName
        console.log('Fetching project/job information for identifier:', jobId);
        try {
          const jobResponse = await api.get(`/jobs/${jobId}`);
          if (jobResponse.data?.success) {
            const jobData = jobResponse.data.data;
            // Support both new (project) and old (job) response formats
            const projectName = jobData.ProjectName || jobData.projectName || 
                               jobData.DisplayName || jobData.displayName ||
                               jobData.JobName || jobData.jobName;
            if (projectName) {
              setProjectName(projectName);
              console.log('Project name loaded:', projectName);
            }
            
            // Always initialize project to prevent state leakage between jobs
            initializeProject(
                { 
                  ...currentProject,
                  ...jobData,
                  projectId: jobData.ProjectId || jobData.projectId || jobId,
                  projectName: projectName,
                  SourceSurveyId: jobData.SourceSurveyId || jobData.sourceSurveyId || null,
                  currentStep: 'cards'
                },
                { markUnsaved: false, markAsSaved: true }
            );
          }
        } catch (jobError) {
          console.error('Error fetching project/job info:', jobError);
        }

        // Fetch existing widgets for this project/job
        // API accepts projectId, fileName, or tableName
        try {
          const widgetsResponse = await api.get(`/widgets/${jobId}`);
          if (widgetsResponse.data?.success) {
            const widgets = widgetsResponse.data.data || [];
            console.log(`✅ Loaded ${widgets.length} existing widgets for job ${jobId}`);
            
            if (widgets.length > 0) {
              const loadedTableData = widgets.map(widget => {
                const wType = toEnum(widget.widgetType);
                const normalizedTags = normalizeTags(widget.tags);
                return {
                  widgetId: widget.widgetId,
                  cardName: widget.widgetName || toDisplay(wType),
                  cardType: wType,
                  cardDataElement: widget.description || `Columns: ${(widget.selectedColumns || []).join(', ')}`,
                  tags: normalizedTags,
                  widgetData: {
                    widgetId: widget.widgetId,
                    widgetName: widget.widgetName,
                    widgetType: wType,
                    selectedColumns: widget.selectedColumns || [],
                    aggregation: widget.aggregation ?? null,
                    description: widget.description || '',
                    prompt: widget.prompt,
                    query: widget.query,
                    tags: normalizedTags
                  }
                };
              });
              
              setTableData(loadedTableData);
              setIsEditingMode(true);
              markProjectAsSaved();
              console.log('✅ Set to editing mode with existing cards');
            } else {
              setIsEditingMode(false);
              console.log('✅ No existing cards, starting in new dashboard mode');
            }
          } else {
            setIsEditingMode(false);
            console.log('✅ No cards found, starting in new dashboard mode');
          }
        } catch (widgetError) {
          // If 404, no widgets exist yet - that's fine, start fresh
          if (widgetError.response?.status === 404) {
            console.log('✅ No existing cards found (404), starting in new dashboard mode');
            setIsEditingMode(false);
          } else {
            throw widgetError; // Re-throw other errors
          }
        }
        
      } catch (error) {
        console.error('❌ Error loading existing cards:', error);
        setLoadingError(
          getApiErrorMessage(error, 'Failed to load existing cards. You can continue creating new cards.')
        );
        setIsEditingMode(false);
      } finally {
        setIsLoadingWidgets(false);
      }
    };

    loadExistingWidgets();
  }, [jobId, api]);

  const handleOpenPopup = (id, initialData = null) => {
    const common = {
      open: true,
      onClose: handleClosePopup,
      jobId,
      initialData,
      onExecute: () => trackChange(CHANGE_TYPES.ADD_CARD, 'Add card'),
      editWidgetId: editingContextRef.current?.widgetId ?? null,
      onUpdateCard: handleUpdateCard
    };
    if (id === 1) {
      setPopupContent(<SummarizeData {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 2) {
      setPopupContent(<AIRecommendation {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 3) {
      setPopupContent(<AnalyzeMajorIssues {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 4) {
      setPopupContent(<SuccessFactors {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 5) {
      setPopupContent(<CustomAICard {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 6) {
      setPopupContent(<BuildBarChart {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 9) {
      setPopupContent(<BuildBarChart {...common} lineMode onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 7) {
      setPopupContent(<BuildPieChart {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    } else if (id === 8) {
      setPopupContent(<BuildKPI {...common} onAddToDashboard={(c) => handleAddToDashboard(id, c)} />);
      setIsPopupOpen(true);
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setPopupContent(null);
    setEditingRowIndex(null);
    setEditInitialData(null);
    editingContextRef.current = { index: null, widgetId: null };
  };

  const handleViewCard = async (index) => {
    const row = tableData[index];
    setViewCardDialog({ open: true, row, payload: null, loading: !!row?.widgetId });
    if (row?.widgetId) {
      try {
        const res = await api.get(`/widgets/by-id/${row.widgetId}`);
        const payload = res.data?.data ?? res.data;
        setViewCardDialog(prev => ({ ...prev, payload, loading: false }));
      } catch (err) {
        console.error('Failed to load card for view:', err);
        setViewCardDialog(prev => ({ ...prev, payload: null, loading: false }));
      }
    } else {
      setViewCardDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const resolvePopupIdFromWidget = (widgetType, chartType) => getPopupIdFromWidgetType(widgetType, chartType);

  const handleEditCard = async (index) => {
    const row = tableData[index];
    setEditingRowIndex(index);
    editingContextRef.current = { index, widgetId: row?.widgetId ?? null };
    // Use widgetData.widgetType (enum) first so the correct popup opens; cardType is display name and would resolve to wrong default
    const widgetTypeForPopup = row?.widgetData?.widgetType ?? row?.cardType;
    const chartTypeForPopup = row?.widgetData?.chartType;
    if (row?.widgetId) {
      try {
        const res = await api.get(`/widgets/by-id/${row.widgetId}`);
        const payload = res.data?.data ?? res.data;
        setEditInitialData(payload);
        const resolvedId = resolvePopupIdFromWidget(payload?.widgetType, payload?.chartType);
        handleOpenPopup(resolvedId, payload);
      } catch (err) {
        console.error('Failed to load card for edit:', err);
        setEditInitialData(null);
        const resolvedId = resolvePopupIdFromWidget(widgetTypeForPopup, chartTypeForPopup);
        handleOpenPopup(resolvedId, row?.widgetData ?? null);
      }
    } else {
      setEditInitialData(null);
      const resolvedId = resolvePopupIdFromWidget(widgetTypeForPopup, chartTypeForPopup);
      handleOpenPopup(resolvedId, row?.widgetData ?? null);
    }
  };

  const handleReupload = () => {
    navigate('/landing');
  };

  const handleUpdateCard = async (data) => {
    const editIndex = editingContextRef.current?.index;
    const widgetId = editingContextRef.current?.widgetId;
    if (editIndex == null || editIndex < 0 || editIndex >= tableData.length || !widgetId) {
      return;
    }
    const existingRow = tableData[editIndex];
    const wType = data?.widgetType ? toEnum(data.widgetType) : toEnum(existingRow?.widgetData?.widgetType) || 'barchart';
    const cardTypeDisplay = toDisplay(wType);
    
    const oldDesc = existingRow?.widgetData?.description || '';
    const cleanOldDesc = oldDesc.startsWith('Columns: ') ? '' : oldDesc;

    const body = {
      widgetTitle: data.widgetName || existingRow?.cardName,
      widgetName: data.widgetName || existingRow?.cardName,
      widgetType: wType,
      selectedColumns: data.selectedColumns ?? existingRow?.widgetData?.selectedColumns ?? [],
      description: data.description ?? cleanOldDesc,
      prompt: data.prompt ?? data.customPrompt ?? existingRow?.widgetData?.prompt ?? '',
      query: data.query ?? existingRow?.widgetData?.query ?? '',
      aggregation: data.aggregation ?? existingRow?.widgetData?.aggregation ?? null
    };
    if (data.results != null) {
      body.widgetResults = data.results;
      body.tags = data.tags ?? (data.results?.tags ? JSON.stringify(data.results.tags) : null);
    }
    try {
      await api.put(`/widgets/${widgetId}`, body);
        const newEntry = {
          widgetId: existingRow?.widgetId ?? widgetId,
          cardName: body.widgetName,
          cardType: cardTypeDisplay,
          cardDataElement: body.description || `Columns: ${(body.selectedColumns || []).join(', ')}`,
          widgetData: { ...(existingRow?.widgetData || {}), ...data, widgetType: wType, widgetName: body.widgetName, selectedColumns: body.selectedColumns, description: body.description }
        };
      const next = [...tableData];
      next[editIndex] = newEntry;
      setTableData(next);
      editingContextRef.current = { index: null, widgetId: null };
      setEditingRowIndex(null);
      setEditInitialData(null);
      clearChangesTracker();
      handleClosePopup();
    } catch (err) {
      console.error('Failed to update widget:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to update card.'));
    }
  };

  const handleAddToDashboard = async (id, data) => {
    const sidebarItems = [
      { id: 5, text: 'Custom AI Card' },
      { id: 1, text: 'Summarize Data' },
      { id: 2, text: 'AI Recommendation' },
      { id: 3, text: 'Analyze Major Issues' },
      { id: 4, text: 'Success Factors' },
      { id: 6, text: 'Build Bar Chart' },
      { id: 9, text: 'Build Line Chart' },
      { id: 7, text: 'Build Pie Chart' },
      { id: 8, text: 'KPI' },
    ];

    const cardType = sidebarItems.find(item => item.id === id)?.text || 'Unknown';
    const widgetTypeEnum = data?.widgetType ? toEnum(data.widgetType) : (id === 8 ? 'kpi' : id === 7 ? 'piechart' : id === 9 ? 'linechart' : id === 6 ? 'barchart' : id === 1 ? 'summary' : id === 2 ? 'recommendation' : id === 3 ? 'major_issues' : id === 4 ? 'success' : id === 5 ? 'custom_ai_card' : 'barchart');

    // Use ref so edit context is correct when callback runs (state may be stale due to async setState before popup opened)
    const editIndex = editingContextRef.current?.index;
    const isEditing = editIndex !== null && editIndex !== undefined && editIndex >= 0 && editIndex < tableData.length;
    const existingRow = isEditing ? tableData[editIndex] : null;
    const widgetId = existingRow?.widgetId ?? editingContextRef.current?.widgetId;

    if (data && typeof data === 'object' && data.widgetType && data.selectedColumns) {
      const wType = widgetTypeEnum || toEnum(data.widgetType);
      
      const oldDesc = existingRow?.widgetData?.description || '';
      const cleanOldDesc = oldDesc.startsWith('Columns: ') ? '' : oldDesc;

      const newEntry = {
        ...(isEditing && (existingRow?.widgetId ?? widgetId) ? { widgetId: existingRow?.widgetId ?? widgetId } : {}),
        cardName: data.widgetName || cardType,
        cardType: toDisplay(wType),
        cardDataElement: data.description || data.notWorkingWell || cleanOldDesc || `Columns: ${(data.selectedColumns || []).join(', ')}`,
        widgetData: { ...data, widgetType: wType, description: data.description || data.notWorkingWell || cleanOldDesc || '' }
      };
      if (isEditing && widgetId) {
        try {
          const body = {
            widgetTitle: data.widgetName || cardType,
            widgetName: data.widgetName || cardType,
            widgetType: wType,
            selectedColumns: data.selectedColumns || [],
            description: data.description ?? data.notWorkingWell ?? existingRow?.widgetData?.description ?? '',
            prompt: data.prompt ?? data.customPrompt ?? existingRow?.widgetData?.prompt ?? '',
            query: data.query ?? existingRow?.widgetData?.query ?? '',
            aggregation: data.aggregation ?? null
          };
          if (data.results != null) {
            body.widgetResults = data.results;
            body.tags = data.tags ?? (data.results?.tags ? JSON.stringify(data.results.tags) : null);
          }
          await api.put(`/widgets/${widgetId}`, body);
          const next = [...tableData];
          next[editIndex] = newEntry;
          setTableData(next);
          editingContextRef.current = { index: null, widgetId: null };
          setEditingRowIndex(null);
          setEditInitialData(null);
          clearChangesTracker(); // Edit persisted to DB
          handleClosePopup();
        } catch (err) {
          console.error('Failed to update card:', err);
          showErrorToast(getApiErrorMessage(err, 'Failed to update card.'));
        }
        return;
      }
      if (isEditing) {
        const next = [...tableData];
        next[editIndex] = newEntry;
        setTableData(next);
        editingContextRef.current = { index: null, widgetId: null };
        setEditingRowIndex(null);
        trackChange(CHANGE_TYPES.EDIT_CARD, 'Edit card'); // In-memory only (no widgetId)
        handleClosePopup();
        return;
      }
      setTableData([...tableData, newEntry]);
      trackChange(CHANGE_TYPES.ADD_CARD, 'Add card'); // Not yet saved to DB
      if (!isEditingMode) {
        setPendingWidgets(prev => [...prev, {
          name: data.widgetName || cardType,
          type: data.widgetType,
          sourceColumns: data.selectedColumns,
          description: data.recommendation || `${data.widgetType} Analysis`,
          query: '',
          results: data.results
        }]);
      }
    } else if (Array.isArray(data)) {
      const oldDesc = existingRow?.widgetData?.description || '';
      const cleanOldDesc = oldDesc.startsWith('Columns: ') ? '' : oldDesc;

      const newEntries = data.map(combination => {
        const wType = toEnum(combination.widgetType);
        return {
          ...(existingRow?.widgetId && combination === data[0] ? { widgetId: existingRow.widgetId } : {}),
          cardName: combination.widgetName || toDisplay(wType),
          cardType: toDisplay(wType),
          cardDataElement: combination.description || combination.notWorkingWell || (existingRow?.widgetId && combination === data[0] ? cleanOldDesc : null) || `Columns: ${(combination.selectedColumns || []).join(', ')}`,
          widgetData: { ...combination, widgetType: wType, description: combination.description || combination.notWorkingWell || (existingRow?.widgetId && combination === data[0] ? cleanOldDesc : '') || '' }
        };
      });
      if (isEditing && newEntries.length > 0) {
        const first = newEntries[0];
        if (widgetId) {
          const firstData = data[0];
          const firstEnum = toEnum(firstData.widgetType);
          try {
            const body = {
              widgetTitle: firstData.widgetName || cardType,
              widgetName: firstData.widgetName || cardType,
              widgetType: firstEnum,
              selectedColumns: firstData.selectedColumns || [],
              description: firstData.description ?? firstData.notWorkingWell ?? cleanOldDesc ?? '',
              prompt: firstData.prompt ?? firstData.customPrompt ?? existingRow?.widgetData?.prompt ?? '',
              query: firstData.query ?? existingRow?.widgetData?.query ?? '',
              widgetResults: firstData.results ?? undefined,
              tags: firstData.tags ?? (firstData.results?.tags ? JSON.stringify(firstData.results.tags) : null),
            };
            await api.put(`/widgets/${widgetId}`, body);
          } catch (err) {
            console.error('Failed to update card:', err);
            showErrorToast(getApiErrorMessage(err, 'Failed to update card.'));
            return;
          }
        }
        const next = [...tableData];
        next[editIndex] = first;
        setTableData(next);
        editingContextRef.current = { index: null, widgetId: null };
        setEditingRowIndex(null);
        setEditInitialData(null);
        clearChangesTracker(); // Edit persisted via put above
        handleClosePopup();
        return;
      }
      setTableData([...tableData, ...newEntries]);
      trackChange(CHANGE_TYPES.ADD_CARD, 'Add card'); // Not yet saved to DB
      
      if (!isEditingMode) {
        data.forEach(combination => {
          if (combination.widgetType && combination.selectedColumns) {
            setPendingWidgets(prev => [...prev, {
              name: combination.widgetName || cardType,
              type: combination.widgetType,
              sourceColumns: combination.selectedColumns,
              description: combination.recommendation || `${combination.widgetType} Analysis`,
              query: '',
              results: combination.results
            }]);
          }
        });
      }
    }
  };

  const handleDeleteEntry = async (index) => {
    const entryToDelete = tableData[index];
    if (isEditingMode && entryToDelete?.widgetId) {
      try {
        console.log('Deleting card from database:', entryToDelete.widgetId);
        await api.delete(`/widgets/${entryToDelete.widgetId}`);
        const newTableData = tableData.filter((_, i) => i !== index);
        setTableData(newTableData);
        setPendingWidgets(prev => prev.filter(w => w.widgetId !== entryToDelete.widgetId));
        clearChangesTracker(); // Delete persisted to DB
      } catch (error) {
        console.error('Error deleting card from database:', error);
        showErrorToast('Failed to delete card from database. Please try again.');
        return;
      }
    } else {
      const newTableData = tableData.filter((_, i) => i !== index);
      setTableData(newTableData);
      if (entryToDelete?.cardName) {
        setPendingWidgets(prev => prev.filter(w => w.cardName !== entryToDelete.cardName));
      }
      trackChange(CHANGE_TYPES.DELETE_CARD, 'Delete card'); // Local-only row; not in DB
    }
  };

  const handleCreateDashboard = async () => {
    if (tableData.length === 0) {
      showAlert('No cards to save. Please add at least one card before creating a dashboard.', { severity: 'warning' });
      return;
    }

    if (isEditingMode && tableData.length > 0) {
      setShowConfirmDialog(true);
      return;
    }

    // New dashboard: open "Add to folder" dialog (required)
    setAddToFolderSelectedIds([]);
    setAddToFolderNewFolderName('');
    setAddToFolderCreateParentId('');
    setAddToFolderCreateError(null);
    setAddToFolderDialogOpen(true);
    await loadAddToFolderFolders();
  };

  const toggleAddToFolderSelection = (folderId) => {
    setAddToFolderSelectedIds((prev) =>
      prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]
    );
  };

  const handleAddToFolderConfirm = () => {
    if (addToFolderSelectedIds.length === 0) {
      showAlert('Please select at least one folder. Every dashboard must be in at least one folder.', { severity: 'warning' });
      return;
    }
    setAddToFolderDialogOpen(false);
    performCreateDashboard({ folderIds: addToFolderSelectedIds });
  };

  const handleAddToFolderCreateFolder = async () => {
    if (!addToFolderNewFolderName.trim() || !addToFolderUserId) return;
    setAddToFolderCreateSubmitting(true);
    setAddToFolderCreateError(null);
    try {
      const res = await api.post('/folder', {
        FolderName: addToFolderNewFolderName.trim(),
        CreatedBy: addToFolderUserId,
        ...(addToFolderCreateParentId && { ParentFolderId: addToFolderCreateParentId }),
      });
      const payload = res.data?.data;
      const row = Array.isArray(payload) ? payload[0] : payload;
      const newFolderId = row?.FolderId || row?.folderId;
      setAddToFolderNewFolderName('');
      await loadAddToFolderFolders();
      if (newFolderId) {
        setAddToFolderSelectedIds((prev) =>
          prev.includes(newFolderId) ? prev : [...prev, newFolderId]
        );
      }
    } catch (e) {
      setAddToFolderCreateError(getApiErrorMessage(e, 'Failed to create folder.'));
    } finally {
      setAddToFolderCreateSubmitting(false);
    }
  };

  const performCreateDashboard = async (options = {}) => {
    const { skipNavigate = false, folderIds = [] } = options;
    setIsSavingCards(true);
    const dashboardIdentifier = jobId || currentJobId;
    if (!dashboardIdentifier) {
      showAlert('No project ID, file name, or table name found. Please try again.', { severity: 'error' });
      setIsSavingCards(false);
      return { success: false, error: 'No project ID found.' };
    }
    try {
      console.log('Creating dashboard with cards:', tableData);
      
      // Prepare widgets for batch save
      const widgetsToSave = tableData.map(entry => {
        const widgetData = entry.widgetData || {};
        return {
          widgetName: entry.cardName || widgetData.widgetName || 'Unnamed Widget',
          widgetType: toEnum(widgetData.widgetType || entry.cardType) || 'barchart',
          selectedColumns: widgetData.selectedColumns || [],
          aggregation: widgetData.aggregation ?? null,
          description: widgetData.description || '',
          prompt: widgetData.customPrompt || widgetData.prompt || '',
          query: widgetData.query || '',
          results: widgetData.results || null
        };
      });
      const response = await api.post('/widgets/batch', {
        projectId: dashboardIdentifier,
        widgets: widgetsToSave,
        dashboardName: projectName || `Dashboard for Project ${dashboardIdentifier}`
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to create dashboard');
      }

      // Add dashboard to folder(s): required when creating new dashboard
      if (folderIds && folderIds.length > 0) {
        const publishRes = await api.post(`/projects/${dashboardIdentifier}/publish`, { folderIds });
        if (!publishRes.data?.success) {
          throw new Error(publishRes.data?.error || publishRes.data?.message || 'Failed to add dashboard to folder(s).');
        }
      }

      // Dashboard now exists, so unlock Dashboard in the workflow breadcrumb for this identifier.
      persistDashboardBreadcrumbUnlock(dashboardIdentifier);

      if (!skipNavigate) {
        await saveProjectProgress('dashboard');
        navigate(`/dashboard/${dashboardIdentifier}`);
      }
      return { success: true };
    } catch (error) {
      console.error('Error creating dashboard:', error);
      const errorMessage = getApiErrorMessage(error, 'Failed to create dashboard. Please try again.');
      showErrorToast(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSavingCards(false);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmDialog(false);
    performCreateDashboard(); // editing: no folderIds, dashboard already exists
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);
  };

  const handleAddToFolderDialogClose = () => {
    setAddToFolderDialogOpen(false);
    setAddToFolderSelectedIds([]);
    setAddToFolderNewFolderName('');
    setAddToFolderCreateParentId('');
    setAddToFolderCreateError(null);
  };

  return (
    <>
      <Breadcrumb currentStep="cards" />
      <Box className="cards-page-container">
        <Container maxWidth="xl" className="cards-main-content">
          {/* Loading State */}
          {isLoadingWidgets && (
            <Box className="cards-loading-state">
              <CircularProgress size={24} />
              <Typography variant="body1" sx={{ fontFamily: branding.fonts.primary }}>
                Loading existing cards...
              </Typography>
            </Box>
          )}

          {/* Editing Mode Info */}
          {isEditingMode && !isLoadingWidgets && (
            <Box className="cards-editing-mode-info">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontFamily: branding.fonts.primary, fontWeight: 600 }}>
                  📝 Editing Dashboard
                </Typography>
                
              </Box>
              <Typography variant="caption" sx={{ fontFamily: branding.fonts.primary }}>
                · {tableData.length} card{tableData.length !== 1 ? 's' : ''} loaded.
              </Typography>
            </Box>
          )}

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" className="cards-project-name" sx={{ mb: 0 }}>
                  Project: {projectName}
                </Typography>
                {currentProject?.SourceSurveyId && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleSyncResponses}
                    disabled={isSyncing}
                    startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                    sx={{ borderRadius: '20px', textTransform: 'none' }}
                  >
                    {isSyncing ? 'Syncing...' : 'Sync Latest Responses'}
                  </Button>
                )}
              </Box>

              <CardsSidebar onAddButtonClick={handleOpenPopup} />
            </Grid>
            <Grid item xs={12} md={8}>
              <CardsTable
                data={tableData}
                onDelete={handleDeleteEntry}
                onView={handleViewCard}
                onEdit={handleEditCard}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {/* empty space for alignment */}
            </Grid>
            <Grid item xs={12} md={8}>
              <CardsControlButtons
                hasEntries={tableData.length > 0}
                onCreateDashboard={handleCreateDashboard}
                isSavingCards={isSavingCards}
                isEditingMode={isEditingMode}
              />
            </Grid>
          </Grid>
        </Container>

        {isPopupOpen && popupContent}

        {/* View Card Dialog - loads by widgetId from API when saved, else uses row data */}
        <Dialog
          open={viewCardDialog.open}
          onClose={() => setViewCardDialog({ open: false, row: null, payload: null, loading: false })}
          maxWidth="sm"
          fullWidth
          aria-labelledby="view-card-title"
        >
          <DialogTitle id="view-card-title" sx={{ fontFamily: branding.fonts.primary }}>
            View Card
          </DialogTitle>
          <DialogContent sx={{ fontFamily: branding.fonts.primary }}>
            {viewCardDialog.row && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {viewCardDialog.payload?.widgetName ?? viewCardDialog.row.cardName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Type: {viewCardDialog.payload?.widgetType ?? viewCardDialog.row.cardType}
                </Typography>
                {viewCardDialog.loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                )}
                {!viewCardDialog.loading && viewCardDialog.payload?.chartData?.length > 0 && (
                  <Box sx={{ mt: 1, height: 280 }}>
                    {viewCardDialog.payload.widgetType === 'kpi' && viewCardDialog.payload.chartData.length === 1 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2, px: 2, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)', cursor: 'default', gap: 0.5, minHeight: '120px' }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: branding.fonts.primary, textAlign: 'center' }}>
                            {viewCardDialog.payload?.widgetName ?? viewCardDialog.row.cardName}
                          </Typography>
                          <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: 'rgb(64, 121, 141)', fontFamily: branding.fonts.primary, lineHeight: 1 }}>
                            {typeof viewCardDialog.payload.chartData[0]?.value === 'number'
                              ? (Number.isInteger(viewCardDialog.payload.chartData[0].value)
                                ? viewCardDialog.payload.chartData[0].value
                                : viewCardDialog.payload.chartData[0].value.toFixed(2))
                              : (viewCardDialog.payload.chartData[0]?.value ?? '—')}
                          </Typography>
                          <Typography sx={{ fontSize: '10px', color: '#9ca3af', fontFamily: branding.fonts.primary, textAlign: 'center', textTransform: 'capitalize', letterSpacing: '0.3px' }}>
                            {viewCardDialog.payload.description || `${viewCardDialog.payload.chartData[0]?.name || viewCardDialog.payload.aggregation}${viewCardDialog.payload.selectedColumns?.[0] ? ` · ${formatBusinessText(viewCardDialog.payload.selectedColumns[0])}` : ''}`}
                          </Typography>
                        </Box>
                    ) : (viewCardDialog.payload.widgetType === 'pie' || viewCardDialog.payload.widgetType === 'pie_chart') ? (() => {
                      let showValueType = 'percentage';
                      let decimalPoints = 1;
                      let customColors = {};
                      try {
                        if (viewCardDialog.payload.query && viewCardDialog.payload.query.startsWith('{')) {
                          const q = JSON.parse(viewCardDialog.payload.query);
                          if (q.showValueType) showValueType = q.showValueType;
                          if (q.decimalPoints !== undefined) decimalPoints = q.decimalPoints;
                          if (q.customColors) customColors = q.customColors;
                        }
                      } catch (e) {}

                      return (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={viewCardDialog.payload.chartData.map((d, i) => ({ ...d, id: d.name + i }))}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ percent, value }) => {
                                if (percent < 0.05) return '';
                                const displayVal = showValueType === 'percentage' ? percent * 100 : value;
                                const formattedVal = Number(displayVal).toFixed(decimalPoints);
                                return `${formattedVal}${showValueType === 'percentage' ? '%' : ''}`;
                              }}
                            >
                              {viewCardDialog.payload.chartData.map((d, i) => {
                                const fill = customColors[d.name] || CHART_COLORS[i % CHART_COLORS.length];
                                return <Cell key={i} fill={fill} />;
                              })}
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      );
                    })() : (() => {
                      const rows = viewCardDialog.payload.chartData;
                      const stacked = pivotStackedBarLongRows(rows);
                      
                      let showValuesOnBars = false;
                      let isSeriesGrouped = false;
                      try {
                        if (viewCardDialog.payload.query && viewCardDialog.payload.query.startsWith('{')) {
                          const q = JSON.parse(viewCardDialog.payload.query);
                          showValuesOnBars = !!q.showValuesOnBars;
                          isSeriesGrouped = q.__groupedBar === 'series';
                        }
                      } catch (e) {
                        // ignore
                      }

                      if (stacked.isStacked && stacked.chartData?.length > 0 && stacked.segmentKeys?.length > 0) {
                        return (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stacked.chartData} margin={{ top: showValuesOnBars ? 16 : 5, right: 5, left: 0, bottom: 30 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="name"
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                tickFormatter={(v) => {
                                  const s = formatBusinessText(v);
                                  return String(s).length > 10 ? String(s).slice(0, 8) + '...' : s;
                                }}
                              />
                              <YAxis width={40} />
                              {!showValuesOnBars && <RechartsTooltip />}
                              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                              {stacked.segmentKeys.map((key, i) => (
                                <Bar
                                  key={key}
                                  dataKey={key}
                                  stackId={isSeriesGrouped ? undefined : 'cardViewStack'}
                                  fill={CHART_COLORS[i % CHART_COLORS.length]} 
                                  name={key} 
                                  label={showValuesOnBars ? { position: 'top', fill: '#000', fontSize: 10, formatter: (val) => typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(2)) : val } : false}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        );
                      }
                      if (viewCardDialog.payload.widgetType === 'linechart' || viewCardDialog.payload.widgetType === 'line' || viewCardDialog.payload.widgetType === 'line_chart') {
                        return (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={rows} margin={{ top: 5, right: 5, left: 0, bottom: 30 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tickFormatter={(v) => (String(v).length > 10 ? String(v).slice(0, 8) + '...' : v)} />
                              <YAxis width={40} />
                              <RechartsTooltip />
                              <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} name="Value" />
                            </LineChart>
                          </ResponsiveContainer>
                        );
                      }
                      return (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={rows} margin={{ top: showValuesOnBars ? 16 : 5, right: 5, left: 0, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tickFormatter={(v) => (String(v).length > 10 ? String(v).slice(0, 8) + '...' : v)} />
                            <YAxis width={40} />
                            {!showValuesOnBars && <RechartsTooltip />}
                            <Bar 
                              dataKey="value" 
                              fill={CHART_COLORS[0]} 
                              name="Value" 
                              label={showValuesOnBars ? { position: 'top', fill: '#000', fontSize: 10, formatter: (val) => typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(2)) : val } : false}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </Box>
                )}
                {!viewCardDialog.loading && viewCardDialog.payload?.widgetResults && (
                  <Box sx={{ mt: 1 }}>
                    <WidgetResultsRenderer
                      results={viewCardDialog.payload.widgetResults}
                      widgetType={viewCardDialog.payload.widgetType}
                      showProcessingInfo={false}
                    />
                  </Box>
                )}
                {!viewCardDialog.loading && !viewCardDialog.payload?.chartData?.length && !viewCardDialog.payload?.widgetResults && viewCardDialog.payload && (
                  <Typography variant="body2" color="text.secondary">No chart data or results for this widget.</Typography>
                )}
                {!viewCardDialog.loading && !viewCardDialog.payload && viewCardDialog.row?.widgetData?.results && (
                  <Box sx={{ mt: 1 }}>
                    {(() => {
                      const raw = viewCardDialog.row.widgetData.results;
                      const parsed = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
                      return parsed ? (
                        <WidgetResultsRenderer results={parsed} widgetType={viewCardDialog.row.widgetData.widgetType} showProcessingInfo={false} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">No results to display.</Typography>
                      );
                    })()}
                  </Box>
                )}
                {!viewCardDialog.loading && !viewCardDialog.payload && viewCardDialog.row && !viewCardDialog.row?.widgetData?.results && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Columns: {Array.isArray(viewCardDialog.row.widgetData?.selectedColumns)
                        ? viewCardDialog.row.widgetData.selectedColumns.map(formatBusinessText).join(', ')
                        : formatBusinessText(viewCardDialog.row.cardDataElement || '—')}
                    </Typography>
                    {viewCardDialog.row.widgetData?.aggregation && (
                      <Typography variant="body2" sx={{ mb: 1 }}>Aggregation: {viewCardDialog.row.widgetData.aggregation}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Save the card first, then View will load chart/results from the server.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewCardDialog({ open: false, row: null, payload: null, loading: false })} sx={{ fontFamily: branding.fonts.primary }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog for Editing Mode */}
        <Dialog
          open={showConfirmDialog}
          onClose={handleCancelSave}
          aria-labelledby="confirm-save-title"
          aria-describedby="confirm-save-description"
        >
          <DialogTitle id="confirm-save-title" sx={{ fontFamily: branding.fonts.primary }}>
            ⚠️ Update Dashboard?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-save-description" sx={{ fontFamily: branding.fonts.primary }}>
              You're about to update an existing dashboard. This will:
              <br />
              • Replace all existing cards with the current ones ({tableData.length} cards)
              <br />
              • Permanently delete any cards you've removed from the list
              <br /><br />
              This action cannot be undone. Are you sure you want to continue?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCancelSave} 
              sx={{ fontFamily: branding.fonts.primary, color: '#666' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSave} 
              variant="contained"
              disabled={isSavingCards}
              startIcon={isSavingCards ? <CircularProgress size={20} /> : null}
              sx={{ 
                fontFamily: branding.fonts.primary,
                backgroundColor: 'rgb(64, 121, 141)',
                '&:hover': { backgroundColor: 'rgb(54, 101, 121)' },
                '&:disabled': { backgroundColor: '#d3d3d3', color: '#a9a9a9' }
              }}
              autoFocus
            >
              {isSavingCards ? 'Updating Dashboard...' : 'Update Dashboard'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add to folder — required when creating a new dashboard */}
        <Dialog open={addToFolderDialogOpen} onClose={handleAddToFolderDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: branding.fonts.primary }}>
            Add to folder
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Every dashboard must be in at least one folder. Select one or more folders for &quot;{projectName || 'Dashboard'}&quot;. You can create a new folder below; it will be selected for you so you can add or remove folders before you create the dashboard.
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Create new folder
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel id="cards-folder-parent-label">Parent location</InputLabel>
              <Select
                labelId="cards-folder-parent-label"
                value={addToFolderCreateParentId}
                label="Parent location"
                onChange={(e) => setAddToFolderCreateParentId(e.target.value)}
                disabled={addToFolderLoading}
              >
                <MenuItem value="">
                  <em>My Dashboards (top level)</em>
                </MenuItem>
                {addToFolderFoldersFlat.map((f) => (
                  <MenuItem key={f.FolderId} value={f.FolderId}>
                    {f.FullPath || f.FolderPath || f.FolderName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
              <TextField
                size="small"
                label="New folder name"
                fullWidth
                value={addToFolderNewFolderName}
                onChange={(e) => setAddToFolderNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddToFolderCreateFolder();
                  }
                }}
                sx={{ fontFamily: branding.fonts.primary }}
              />
              <Button
                variant="outlined"
                onClick={handleAddToFolderCreateFolder}
                disabled={
                  !addToFolderNewFolderName.trim()
                  || !addToFolderUserId
                  || addToFolderCreateSubmitting
                  || addToFolderLoading
                }
                sx={{ flexShrink: 0, mt: 0.5, fontFamily: branding.fonts.primary }}
              >
                {addToFolderCreateSubmitting ? 'Creating…' : 'Create'}
              </Button>
            </Box>
            {!addToFolderUserId && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Loading account for folder creation…
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Select folder(s)
            </Typography>
            {addToFolderLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            ) : addToFolderFoldersFlat.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No folders yet. Create one above, then select it to continue.
              </Typography>
            ) : (
              <List dense>
                {addToFolderFoldersFlat.map((f) => (
                  <ListItemButton
                    key={f.FolderId}
                    onClick={() => toggleAddToFolderSelection(f.FolderId)}
                    selected={addToFolderSelectedIds.includes(f.FolderId)}
                  >
                    <ListItemIcon>
                      <FolderIcon />
                    </ListItemIcon>
                    <ListItemText primary={f.FolderName} secondary={f.FolderPath || f.FullPath} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddToFolderDialogClose} sx={{ fontFamily: branding.fonts.primary }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddToFolderConfirm}
              disabled={addToFolderLoading || addToFolderSelectedIds.length === 0 || isSavingCards}
              sx={{
                fontFamily: branding.fonts.primary,
                backgroundColor: 'rgb(64, 121, 141)',
                '&:hover': { backgroundColor: 'rgb(54, 101, 121)' },
              }}
            >
              {isSavingCards ? 'Creating…' : 'Create dashboard'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CardsPage;
